// src/pages/ConfirmSubscription.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Alert,
    CircularProgress,
    Button,
    Stack,
    Link,
} from '@mui/material';
import { apiFetch, API_BASE } from '../utils/api';

const isHex64 = (t) => typeof t === 'string' && /^[a-f0-9]{64}$/i.test(t);

export default function ConfirmSubscription() {
    const [params] = useSearchParams();
    const token = params.get('token');

    const [status, setStatus] = useState({ loading: true, error: '', message: '' });
    const [unsubmitting, setUnsubmitting] = useState(false);
    const [unsubscribed, setUnsubscribed] = useState(false);

    const tokenValid = useMemo(() => isHex64(token || ''), [token]);

    useEffect(() => {
        if (!token) {
            setStatus({ loading: false, error: 'No confirmation token provided.', message: '' });
            return;
        }
        if (!tokenValid) {
            setStatus({ loading: false, error: 'Invalid confirmation token.', message: '' });
            return;
        }

        (async () => {
            try {
                const res = await apiFetch(`/api/newsletter/confirm?token=${token}`);
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data?.error || 'Confirmation failed.');
                setStatus({
                    loading: false,
                    error: '',
                    message: data.message || 'Subscription confirmed.',
                });
            } catch (err) {
                setStatus({ loading: false, error: err.message || 'Confirmation failed.', message: '' });
            }
        })();
    }, [token, tokenValid]);

    const handleUnsubscribe = async () => {
        if (!tokenValid) return;
        try {
            setUnsubmitting(true);
            const res = await apiFetch('/api/newsletter/unsubscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || 'Unable to unsubscribe right now.');
            setUnsubscribed(true);
            setStatus({
                loading: false,
                error: '',
                message:
                    data.message || 'You have been unsubscribed from the newsletter.',
            });
        } catch (err) {
            setStatus({
                loading: false,
                error: err.message || 'Unable to unsubscribe right now.',
                message: '',
            });
        } finally {
            setUnsubmitting(false);
        }
    };

    if (status.loading) {
        return (
            <Container sx={{ textAlign: 'center', mt: { xs: 6, md: 10 }, px: { xs: 2, md: 0 } }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Confirming your subscription...</Typography>
            </Container>
        );
    }

    // HTML unsubscribe endpoint must live on the API origin
    const apiUnsubscribeHref = tokenValid
        ? `${API_BASE}/api/newsletter/unsubscribe?token=${token}`
        : '#';

    return (
        <Container
            maxWidth="sm"
            sx={{ my: { xs: 4, md: 8 }, px: { xs: 2, md: 0 }, textAlign: 'center' }}
        >
            <Paper elevation={1} sx={{ p: { xs: 2.5, sm: 3, md: 4 }, borderRadius: 2 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: '1.6rem', sm: '1.8rem' },
                        lineHeight: { xs: 1.25, sm: 1.25 },
                    }}
                >
                    Newsletter Subscription
                </Typography>

                {status.error ? (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {status.error}
                    </Alert>
                ) : (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        {status.message}
                    </Alert>
                )}

                {status.error ? (
                    <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: { xs: '1rem' } }}>
                        If you think this is a mistake, try the link again from your email or sign up once
                        more.
                    </Typography>
                ) : (
                    <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: { xs: '1rem' } }}>
                        {!unsubscribed ? (
                            <>
                                You’re all set! We’ll send occasional updates about exhibits and events. If this
                                wasn’t you, you can{' '}
                                <Link href={apiUnsubscribeHref} rel="noopener noreferrer nofollow">
                                    unsubscribe
                                </Link>
                                .
                            </>
                        ) : (
                            'Your address has been removed from our mailing list.'
                        )}
                    </Typography>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                    <Button
                        variant="contained"
                        component={RouterLink}
                        to="/"
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Back to homepage
                    </Button>

                    {!status.error && tokenValid && !unsubscribed && (
                        <Button
                            variant="outlined"
                            onClick={handleUnsubscribe}
                            disabled={unsubmitting}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                            {unsubmitting ? 'Unsubscribing…' : 'Unsubscribe'}
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Container>
    );
}
